import * as React from "react";
import Box from "@mui/material/Box";
import { Button, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { enGB } from "date-fns/locale";
import moment from "moment-timezone";
import { default as DataGrid } from "../../../components/LocalizedDataGrid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTranslation } from "react-i18next";
import CoGenSavingsWithBaseline from "../../../components/CoGenSavingsWithBaseline";
import CoGenPlanTimeline from "./CoGenPlanTimeline";

import Chart from "react-apexcharts";
import { ApiContext } from "../../../App";
import { useLocation } from "react-router-dom";
import ReactECharts from "echarts-for-react";


const DailyChart = (props) => {
	const { data, localTimezone, currencySymbol, cost } = props;

	const { t } = useTranslation();

	let series;
	let timestamps = []
	if (cost) {

		const solarSavings = data?.solar_savings_through_time?.map((item) => item.value)
		const baseline = data?.total_cost_through_time_baseline?.map((item) => item.value)
		const baselineWithSolar = baseline?.map((item, index) => item + solarSavings[index])
		const manualBaseline = data?.manual_costs_through_time?.map((item) => item.value)
		const optimized = data?.total_cost_through_time?.map((item) => item.value.toFixed(2))

		series = [
			{
				name: t("Solar"),
				data: baseline,
				type: "line",
			},
			{
				name: t("Grid"),
				data: baselineWithSolar,
				type: "line",
			},
			{
				name: t("Cogeneration (manual)"),
				data: manualBaseline,
				type: "line",
			},
			{
				name: t("Optimised"),
				data: optimized,
				type: "line",
			},
		];
		
		timestamps = data?.solar_savings_through_time?.map(item => moment.utc(item.start).tz(localTimezone).format("HH:mm"))
		

	} else {

		timestamps = data?.grid_solution?.map(item => moment.utc(item.start).tz(localTimezone).format("HH:mm"))

		const grid = data?.grid_solution?.map((item) => item.consumption * 2.0); // we multiply by 2 to turn MWh into MW (each x element covers half an hour)
		const coGen = data?.grid_solution?.map((item, index) => data.solution.reduce((X, Y) => X + Y.values[index].value, 0));

		const solar = data?.solar_generation_through_time?.map((item) =>  item.value * 2.0); // we multiply by 2 to turn MWh into MW (each x element covers half an hour)

		const total = grid?.map((item, index) => item + coGen[index] + solar[index]);

		series = [
			{
				name: t("Total"),
				data: total,
				type: "line",
				symbol: 'none',
				lineStyle: {
					width: 4
				}
			},
			{
				name: t("Co-generation"),
				data: coGen,
				type: "line",
				symbol: 'none',
				lineStyle: {
					width: 4
				}
			},
			{
				name: t("Grid"),
				data: grid,
				type: "line",
				symbol: 'none',
				lineStyle: {
					width: 4
				}
			},
			{
				name: t("Solar"),
				data: solar,
				type: "line",
				symbol: 'none',
				lineStyle: {
					width: 4
				}
			},
		];
	}

	const chartUnit = cost ? currencySymbol : 'MW'
	const formatValueWithUnit = (value, cost, symbol) => {
		if (symbol == '£') return symbol + value
		return value + symbol
	}

	let options = {
        grid: {
            top: '10%',
			bottom: '10%',
			containLabel: true,
			right: 30,
			left: 30,

        },
        xAxis: {
            data: timestamps,
        },
        yAxis: {
            min: 0,
			axisLabel: {
				formatter: (value) => formatValueWithUnit(value, cost, chartUnit)
			}
        },
        legend: {
            show: true,
            bottom: 0,
			left: "center",
        },
        tooltip: {
            trigger: "axis",
            valueFormatter:  (value) => {
                const truncatedValue = typeof value == "number" ? value.toLocaleString(navigator.language,{
                    minimumFractionDigits:2,
                    maximumFractionDigits:2
                }) : value
                const truncatedValueWithCurrency = formatValueWithUnit(truncatedValue, cost, chartUnit)
                return truncatedValueWithCurrency
            } 
        },
		textStyle: {
			fontFamily: "Roboto, Helvetica, Arial, sans-serif" 
		},
        series
    }


	return (
		<>
			{data.date ? (
				<ReactECharts style={{ width: '100%', height: '100%' }} option={options}/>
			) : null}
		</>
	);
};

const Events = (props) => {
	const { events, localTimezone } = props;

	const { t } = useTranslation();

	const columns = [
		{
			field: "timestamp",
			headerName: t("Time"),
			flex: 1,
			minWidth: 80,
			valueFormatter: (params) => {
				return moment.utc(params.value).tz(localTimezone).format("HH:mm");
			},
		},
		{
			field: "generator_identifier",
			headerName: t("Generator ID"),
			flex: 1,
			minWidth: 80,
		},
		{ field: "generator_name", headerName: t("Device"), flex: 1, minWidth: 80 },
		{
			field: "generator_location",
			headerName: t("Location"),
			flex: 1,
			minWidth: 80,
		},
		{
			field: "value",
			headerName: t("SetPoint"),
			flex: 1,
			minWidth: 80,
			valueFormatter: (params) => {
				return params.value + " MW";
			},
		},
	];

	return (
		<DataGrid
			columns={columns}
			rows={events}
			getRowId={(row) => row.url}
			initialState={{
				sorting: {
					sortModel: [{ field: "timestamp", sort: "asc" }],
				},
			}}
		/>
	);
};

export default function CogenPlanView(props) {
	const localTimezone = props.clientInfo.timezone;
	const currencySymbol = props.clientInfo.currency_symbol;

	const { facility } = props;

	const { t } = useTranslation();

	const [date, setDate] = React.useState(new Date());
	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [planData, setPlanData] = React.useState({});
	const [events, setEvents] = React.useState([]);
	const [lastUpdated, setLastUpdated] = React.useState(undefined);
	const [waiting, setWaiting] = React.useState(false);
	const [chartType, setChartType] = React.useState("MW");

	const {cogenPlanApi, cogenEventApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		cogenPlanApi.getCoGenPlanFromDateFacility(
			date,
			facility,
			(data) => {
				setPlanData(data);
			},
			() => {
				setAlertMessage(t("Couldn't connect to server."));
				setAlertOpen(true);
				setPlanData({});
			}
		);

		cogenEventApi.getCoGenEvents(
			facility,
			date,
			(data) => {
				setEvents(data);
			},
			() => {
				setAlertMessage(t("Couldn't connect to server."));
				setAlertOpen(true);
				setEvents([]);
			}
		);
	}, [date, lastUpdated, facility]);

	const handleGenerate = async () => {
		const task_id = await cogenPlanApi.generateAsyncCoGenPlan(
			facility,
			date,
			() => {
				setWaiting(true);
			},
			() => {
				setWaiting(false);
			},
			() => {},
			(message) => {
				setAlertMessage(t("Problem generating plan."));
				setAlertOpen(true);
			}
		);
		if (task_id) {
			const startTime = Date.now();
			let coGenPlanGenerationFinished = false;
			while (
				!coGenPlanGenerationFinished &&
				Date.now() - startTime < 300 * 1000
			) {
				cogenPlanApi.getCoGenStatus(task_id, (data) => {
					if (data == "SUCCESS") {
						setLastUpdated(Date());
						setWaiting(false);
						coGenPlanGenerationFinished = true;
						return;
					}
					if (data == "FAILURE") {
						alert("Generating co gen plan failed");
						setWaiting(false);
						coGenPlanGenerationFinished = true;
						return;
					}
				});
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
			if (coGenPlanGenerationFinished == false) {
				setWaiting(false);
				alert(t("waitingForPlan"));
			}
		}
	};

	const DateSelector = () => {
		return (
			<Stack
				spacing={2}
				direction={{ xs: "column", md: "row" }}
			>
				<Typography
					component="h2"
					variant="h6"
					color="primary"
				>
					{t("Date")}
				</Typography>

				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={enGB}
				>
					<DatePicker
						value={date}
						onChange={(newValue) => {
							setDate(newValue);
						}}
						renderInput={(params) => <TextField {...params} />}
					/>
				</LocalizationProvider>
				<Button
					variant="contained"
					onClick={() => handleGenerate()}
					disabled={waiting}
				>
					{waiting ? <CircularProgress /> : t("Generate plan")}
				</Button>
			</Stack>
		);
	};

	const calculateConsumption = (planData) => {
		let total = 0;

		planData?.grid_solution?.map((s) => (total += s.consumption));

		return total;
	};

	return (
		<Box
			sx={{
				backgroundColor: (theme) =>
					theme.palette.mode === "light"
						? theme.palette.grey[100]
						: theme.palette.grey[900],
				overflow: "auto",
			}}
		>
			<Container sx={{ mt: 4, mb: 4 }}>
				<Grid
					container
					spacing={2}
					alignItems="stretch"
					direction="row"
				>
					<Grid item>
						<Paper
							sx={{
								p: 2,
							}}
						>
							<DateSelector />
						</Paper>
					</Grid>
					<Grid
						item
						xs={12}
						md={8}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<Stack
								direction={"row"}
								spacing={2}
							>
								<Typography
									component="h2"
									variant="h6"
									color="primary"
									gutterBottom
								>
									{t("Cost profile")}
								</Typography>
								<ToggleButtonGroup
									value={chartType}
									exclusive
									size="small"
									onChange={(event, newValue) => setChartType(newValue)}
								>
									<ToggleButton value="MW">{"MW"}</ToggleButton>
									<ToggleButton value="cost">{currencySymbol}</ToggleButton>
								</ToggleButtonGroup>
							</Stack>
							<Box height={450}>
								<DailyChart
									data={planData}
									localTimezone={localTimezone}
									currencySymbol={currencySymbol}
									cost={chartType === "cost"}
								/>
							</Box>
						</Paper>
					</Grid>
					<Grid
						item
						xs={12}
						md={4}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<CoGenSavingsWithBaseline
								allGridCost={
									planData.total_cost_baseline + planData.total_solar_savings ??
									0.0
								}
								allGridConsumption={
									planData.total_generator_consumption +
										planData.total_grid_consumption +
										planData.total_solar_generation ?? 0.0
								}
								allGridCO2={
									planData.co2_baseline + planData.co2_savings_solar ?? 0.0
								}
								gridSolarCost={planData.total_cost_baseline ?? 0.0}
								gridSolarConsumption={
									planData.total_generator_consumption +
										planData.total_grid_consumption ?? 0.0
								}
								gridSolarCO2={planData.co2_baseline ?? 0.0}
								coGenManualCost={planData.total_manual_cost ?? 0.0}
								coGenManualConsumption={
									planData.manual_grid_solution?.reduce(
										(x, y) => x + y["value"],
										0.0
									) ?? 0.0
								}
								coGenManualCO2={planData.co2_manual ?? 0.0}
								optimizedCost={planData.total_cost ?? 0.0}
								optimizedConsumption={planData.total_grid_consumption ?? 0.0}
								optimizedCO2={planData.co2 ?? 0.0}
								dateString={date.toLocaleDateString()}
								currencySymbol={currencySymbol}
								title={t("Forecasted savings")}
							/>
						</Paper>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<Paper sx={{ p: 2 }}>
							<Typography
								component="h2"
								variant="h6"
								color="primary"
								gutterBottom
							>
								{t("Generators")}
							</Typography>
							<Box>
								<CoGenPlanTimeline
									events={events}
									localTimezone={localTimezone}
								/>
							</Box>
						</Paper>
					</Grid>

					<Grid
						item
						xs={12}
					>
						<Paper
							sx={{
								p: 2,
							}}
						>
							<Typography
								component="h2"
								variant="h6"
								color="primary"
								gutterBottom
							>
								{t("Events")}
							</Typography>
							<Box height={400}>
								<Events
									events={events}
									localTimezone={localTimezone}
								/>
							</Box>
						</Paper>
					</Grid>
				</Grid>
			</Container>

			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Problem")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
